import React, { FocusEvent, ChangeEvent, JSX, ReactElement, useState } from 'react';
import { Input, InputProps, Label } from 'reactstrap';
import { InputType } from 'reactstrap/types/lib/Input';
import { Controller, ControllerRenderProps, FieldErrors, FieldValues } from 'react-hook-form';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheck,
  faExclamationTriangle,
  faEye,
  faEyeSlash
} from '@fortawesome/free-solid-svg-icons';
import { useFormContext } from '../Form';
import { BaseFormElement } from '../types/types';
import useConfig from '../../Hooks/useConfig';
import useAppService from '../../Hooks/useAppService';
import dayjs from 'dayjs';
import useMediaQuery, { MEDIAQUERY_DEFAULTS } from '../../Hooks/useMediaQuery';
import classNames from 'classnames';
import { sanitize } from '@ibe/services';
import { getErrorMessage } from './utils';

interface Props {
  id?: string;
  tag: BaseFormElement['tag'];
  name: string;
  ariaLabel?: string;
  baseName?: string;
  label?: string;
  boldLabel?: boolean;
  className?: string;
  errorsProp?: FieldErrors;
  type?: InputType;
  autoComplete?: string;
  showErrors?: boolean;
  showErrorIcon?: boolean;
  disablePaste?: boolean;
  onFocus?: (e: FocusEvent<HTMLInputElement>) => void;
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
  onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
  ignoreTypeSwitchInMobile?: boolean;
  ignoreTypeSwitchInDesktop?: boolean;
  customCheckboxElement?: ReactElement;
  helperText?: string;
  validateOnBlurOnly?: boolean;
  showSuccessState?: boolean;
}

const FormInput = (props: Props & Omit<InputProps, 'tag'>): JSX.Element | null => {
  const {
    id,
    tag,
    name,
    baseName,
    ariaLabel,
    label,
    boldLabel,
    className,
    errorsProp,
    type,
    autoComplete,
    disabled,
    placeholder,
    showErrors,
    showErrorIcon,
    disablePaste,
    onFocus,
    onBlur,
    onChange,
    ignoreTypeSwitchInMobile,
    ignoreTypeSwitchInDesktop,
    customCheckboxElement,
    helperText,
    validateOnBlurOnly,
    showSuccessState
  } = props;

  const config = useConfig();
  const app = useAppService();
  const { register, errors, control, formState, trigger } = useFormContext<FieldValues>();
  const { touchedFields, dirtyFields, isSubmitted } = formState || {};
  const { _formValues } = control || {};
  const [isDateField, setIsDateField] = useState<boolean>(false);
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [hasBeenBlurred, setHasBeenBlurred] = useState<boolean>(false);
  const isDesktop = useMediaQuery({ type: 'min', query: MEDIAQUERY_DEFAULTS.md });
  const [showPassword, setShowPassword] = useState<Record<string, boolean>>({});

  const isPasswordVisible = (fieldName: string) => showPassword[fieldName];

  const togglePassword = (fieldName: string) => {
    setShowPassword(prev => ({ ...prev, [fieldName]: !prev[fieldName] }));
  };
  function sanitizeForId(input?: string): string {
    if (input) {
      return input
        .toLowerCase()
        .replace(/[^\w]+/g, '')
        .trim();
    }
    return '';
  }

  const placeHolderSanitized = sanitizeForId(placeholder);
  const elementId = `form-builder-input-${
    id ? `${id}-${name}` : name
  }-${tag}-${placeHolderSanitized}`;

  if (!register) return null;

  const formErrors = errorsProp || errors;
  const fieldName = baseName ?? name;
  const fieldError = formErrors?.[fieldName];
  const errorMessage = getErrorMessage(fieldError);

  const hasError = !!formErrors?.[fieldName];
  const isTouched = !!touchedFields?.[fieldName];
  const fieldValue = _formValues?.[fieldName];
  const hasValue = typeof fieldValue === 'string' ? fieldValue.length > 0 : !!fieldValue;

  const displayError = validateOnBlurOnly
    ? showErrors && hasError && (isSubmitted || isTouched || hasBeenBlurred) && !isFocused
    : showErrors && hasError;

  const displaySuccess = showSuccessState ? !hasError && hasValue && !isFocused : false;

  const handleFocus = (e: FocusEvent<HTMLInputElement>) => {
    setIsFocused(true);
    if (tag === 'dateInput') {
      setIsDateField(true);
    }
    if (!!onFocus) {
      onFocus(e);
    }
  };

  const handleBlur = async (e: FocusEvent<HTMLInputElement>) => {
    setIsFocused(false);
    setHasBeenBlurred(true);

    if (trigger) {
      await trigger(fieldName);
    }

    if (tag === 'dateInput') {
      setIsDateField(false);
    }
    if (!!onBlur) {
      onBlur(e);
    }
  };

  const getValue = (value: string): string => {
    if (
      tag === 'dateInput' &&
      !isDateField &&
      !!value &&
      (!ignoreTypeSwitchInMobile || (ignoreTypeSwitchInMobile && isDesktop)) &&
      (!ignoreTypeSwitchInDesktop || (ignoreTypeSwitchInDesktop && !isDesktop))
    ) {
      return dayjs(value).format(config.displayFormatDate[app.lang]);
    } else {
      return value;
    }
  };

  const renderInput = (field: ControllerRenderProps<FieldValues, string>): ReactElement => {
    const isPasswordField = type === 'password';
    const visible = isPasswordField ? isPasswordVisible(name) : false;
    let inputType: InputType = type ?? 'text';

    if (isPasswordField) {
      inputType = visible ? 'text' : 'password';
    } else {
      const shouldForceDateType =
        isDateField ||
        (tag === 'dateInput' &&
          ((ignoreTypeSwitchInMobile && !isDesktop) || (ignoreTypeSwitchInDesktop && isDesktop)));

      if (shouldForceDateType) {
        inputType = 'date';
      }
    }

    const inputEl = (
      <Input
        id={elementId}
        /* eslint-disable-next-line */
        {...field}
        data-testid={'form-input-' + name}
        value={getValue(field.value)}
        aria-label={ariaLabel ?? placeholder ?? ''}
        type={inputType}
        onFocus={handleFocus}
        onBlur={async e => {
          field.onBlur();
          await handleBlur(e);
        }}
        onChange={e => {
          field.onChange(e);
          onChange?.(e);
        }}
        className={`${className || ''} ${!!field.value ? 'input--filled' : ''} ${
          !!customCheckboxElement ? 'input--hidden' : ''
        }`}
        autoComplete={autoComplete}
        disabled={disabled}
        placeholder={placeholder}
        onPaste={(e): void => {
          if (disablePaste) {
            e.preventDefault();
          }
        }}
      />
    );

    if (!isPasswordField) return inputEl;
    return (
      <div className="form__password__wrap">
        {inputEl}
        <button
          type="button"
          className="form__password__wrap__toggle"
          onClick={() => togglePassword(name)}
          aria-label={visible ? 'Hide' : 'Show'}
        >
          <FontAwesomeIcon icon={visible ? faEye : faEyeSlash} />
        </button>
      </div>
    );
  };
  return (
    <div
      className={classNames('form-input', 'form-input-' + name, {
        'input--error': displayError,
        'form-input--errors': displayError,
        'form-input--touched': isTouched,
        'form-input--dirty': dirtyFields?.[fieldName],
        'form-input--form-submitted': isSubmitted,
        'form-input--success': displaySuccess
      })}
    >
      {!!label && !customCheckboxElement && (
        <Label
          className={`${displayError ? 'form__error__top ' : ''}${
            boldLabel ? ' font-weight-bold' : ''
          }`}
          htmlFor={elementId}
          dangerouslySetInnerHTML={{ __html: sanitize(label, true) }}
        />
      )}
      <Controller
        name={name}
        control={control}
        render={({ field }): JSX.Element =>
          tag !== 'dateInput' ? (
            <>
              {type === 'checkbox' && !!customCheckboxElement && !!label ? (
                <Label
                  className={`custom-checkbox-label ${displayError ? 'form__error__top ' : ''}${
                    boldLabel ? ' font-weight-bold' : ''
                  }`}
                  htmlFor={elementId}
                >
                  <div
                    dangerouslySetInnerHTML={{
                      __html: sanitize(label, true)
                    }}
                  />
                  {renderInput(field)}
                </Label>
              ) : (
                renderInput(field)
              )}
            </>
          ) : (
            <div className="position-relative">
              {renderInput(field)}
              {tag === 'dateInput' &&
                ((ignoreTypeSwitchInMobile && !isDesktop) ||
                  (ignoreTypeSwitchInDesktop && isDesktop)) &&
                !isDateField &&
                !field.value &&
                !!placeholder && (
                  <div
                    className="form__input__placeholder"
                    onClick={(): void => {
                      setIsDateField(true);
                      document.getElementById(elementId)?.focus();
                      document.getElementById(elementId)?.click();
                    }}
                  >
                    {placeholder}
                  </div>
                )}
            </div>
          )
        }
      />
      {!displayError && (!showErrorIcon || !displayError) && helperText && (
        <div className="form__helper-text">{helperText}</div>
      )}
      {displayError && errorMessage ? <div className="form__error">{errorMessage}</div> : null}
      {showErrorIcon && displayError && (
        <FontAwesomeIcon icon={faExclamationTriangle} className="form-input__error-icon" />
      )}
      {displaySuccess && <FontAwesomeIcon icon={faCheck} className="form-input__success-icon" />}
      {!formErrors?.[fieldName] &&
        (isTouched || isSubmitted) &&
        !!_formValues?.[fieldName] &&
        !showSuccessState && <span className="form-input__success" />}
    </div>
  );
};

export default FormInput;
